import type { SlaTimerSummary, TicketDetail } from '@aurora/contracts';
import { Clock } from 'lucide-react';
import { absoluteTime, shortDate, untilDeadline } from '../../lib/format';
import { cn } from '../../lib/cn';
import { Badge } from '../../components/ui';

/**
 * Service levels on one ticket, read from each target's own clock.
 *
 * The ticket's due-date columns survive a target being met or paused, and the
 * rail used to colour them by the wall clock alone — so a response sent on time
 * showed as "1d overdue" in red, and a paused ticket counted down to a breach
 * that could not happen. Each target now says what is true of it: met (on time
 * or late), breached, paused, or running with the time left. The header chip
 * is derived from the same rows, so the two can never disagree.
 */

type Tone = 'neutral' | 'positive' | 'caution' | 'critical' | 'informative';

export interface TargetView {
  target: SlaTimerSummary['target_type'];
  name: string;
  /** What to print. */
  text: string;
  tone: Tone;
  /** The moment the text is about, for a tooltip with the exact time. */
  at: string | null;
}

const TARGET_NAMES: Record<SlaTimerSummary['target_type'], string> = {
  first_response: 'First response',
  next_response: 'Next response',
  resolution: 'Resolution',
};

export function describeTarget(timer: SlaTimerSummary): TargetView {
  const name = TARGET_NAMES[timer.target_type];
  switch (timer.state) {
    case 'met': {
      const late = Boolean(timer.met_at && timer.due_at && new Date(timer.met_at) > new Date(timer.due_at));
      return {
        target: timer.target_type,
        name,
        text: `${late ? 'Met late' : 'Met'} · ${shortDate(timer.met_at)}`,
        tone: late ? 'caution' : 'positive',
        at: timer.met_at,
      };
    }
    case 'breached':
      return {
        target: timer.target_type,
        name,
        text: `Breached · was due ${shortDate(timer.due_at)}`,
        tone: 'critical',
        at: timer.breached_at ?? timer.due_at,
      };
    case 'paused':
      return { target: timer.target_type, name, text: 'Paused', tone: 'neutral', at: timer.paused_at };
    default: {
      const deadline = untilDeadline(timer.due_at);
      if (!deadline) return { target: timer.target_type, name, text: 'Running', tone: 'informative', at: null };
      return {
        target: timer.target_type,
        name,
        text: `${shortDate(timer.due_at)} · ${deadline.label}`,
        tone: deadline.overdue ? 'critical' : timer.at_risk || deadline.imminent ? 'caution' : 'informative',
        at: timer.due_at,
      };
    }
  }
}

/**
 * The one line that sums a ticket's service level up, for its header.
 *
 * The nearest running deadline when there is one ("Resolution due in 2h"),
 * otherwise what is true of the targets as a whole: breached, paused, or met.
 */
export function slaHeadline(ticket: TicketDetail): { text: string; tone: Tone } | null {
  const timers = ticket.sla_timers;
  if (!timers) return null;
  if (timers.length === 0) return null;

  const running = timers
    .filter((timer) => timer.state === 'running' && timer.due_at)
    .sort((a, b) => new Date(a.due_at!).getTime() - new Date(b.due_at!).getTime());
  const next = running[0];
  if (next) {
    const deadline = untilDeadline(next.due_at)!;
    const name = TARGET_NAMES[next.target_type];
    const text = deadline.overdue
      ? `${name} overdue by ${deadline.label.replace(' overdue', '')}`
      : deadline.label === 'due now'
        ? `${name} due now`
        : `${name} due ${deadline.label}`;
    return {
      text,
      tone: deadline.overdue ? 'critical' : next.at_risk || deadline.imminent ? 'caution' : 'informative',
    };
  }
  if (timers.some((timer) => timer.state === 'breached')) return { text: 'SLA breached', tone: 'critical' };
  if (timers.some((timer) => timer.state === 'paused')) return { text: 'SLA paused', tone: 'neutral' };
  const met = timers.filter((timer) => timer.state === 'met');
  if (met.length > 0) {
    const late = met.some((timer) => timer.met_at && timer.due_at && new Date(timer.met_at) > new Date(timer.due_at));
    return late ? { text: 'SLA met late', tone: 'caution' } : { text: 'SLA met', tone: 'positive' };
  }
  return null;
}

export function SlaChip({ ticket }: { ticket: TicketDetail }) {
  const headline = slaHeadline(ticket);
  if (!headline) return null;
  return (
    <Badge tone={headline.tone} dot>
      <Clock aria-hidden className="size-3" />
      {headline.text}
    </Badge>
  );
}

const TEXT_TONES: Record<Tone, string> = {
  neutral: 'text-ink-muted',
  positive: 'text-positive-ink',
  caution: 'text-caution-ink',
  critical: 'text-critical-ink',
  informative: 'text-ink',
};

/** Each target on its own row, in the rail. */
export function SlaTargets({ ticket }: { ticket: TicketDetail }) {
  const timers = ticket.sla_timers ?? [];

  return (
    <section aria-labelledby="sla-heading">
      <h2 id="sla-heading" className="mb-1 text-reading font-semibold text-ink">
        Service level
      </h2>
      {timers.length === 0 ? (
        <p className="py-1 text-body text-ink-muted">No service level applies to this ticket.</p>
      ) : (
        <dl className="divide-y divide-line">
          {timers.map((timer) => {
            const view = describeTarget(timer);
            return (
              <div key={timer.target_type} className="grid grid-cols-[7.5rem_1fr] items-start gap-2 py-1.5">
                <dt className="pt-0.5 text-mini text-ink-subtle">{view.name}</dt>
                <dd
                  className={cn('text-body font-medium', TEXT_TONES[view.tone])}
                  title={view.at ? absoluteTime(view.at) : undefined}
                >
                  {view.text}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </section>
  );
}
